// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../AuthContext';
import { AlertTriangle, Camera, X, Send, CheckCircle, Filter } from 'lucide-react';

const INCIDENT_TYPES = [
  { value: 'spill',               label: 'Spill',               icon: '💧' },
  { value: 'hazard',              label: 'Hazard',              icon: '⚠️' },
  { value: 'pest',                label: 'Pest',                icon: '🐛' },
  { value: 'equipment_failure',   label: 'Equipment Failure',   icon: '🔧' },
  { value: 'temperature',         label: 'Temperature Issue',   icon: '🌡️' },
  { value: 'cross_contamination', label: 'Cross-Contamination', icon: '🦠' },
  { value: 'other',               label: 'Other',               icon: '📋' },
];

const SEVERITY_CONFIG = {
  low:      { label: 'Low',      color: '#94A3B8', bg: '#F8FAFC' },
  medium:   { label: 'Medium',   color: '#F59E0B', bg: '#FEF3C7' },
  high:     { label: 'High',     color: '#EF4444', bg: '#FEE2E2' },
  critical: { label: 'Critical', color: '#DC2626', bg: '#FEE2E2' },
};

const STATUS_CONFIG = {
  OPEN:        { label: 'Open',        color: '#EF4444', bg: '#FEE2E2' },
  IN_PROGRESS: { label: 'In Progress', color: '#F59E0B', bg: '#FEF3C7' },
  RESOLVED:    { label: 'Resolved',    color: '#10B981', bg: '#D1FAE5' },
  CLOSED:      { label: 'Closed',      color: '#94A3B8', bg: '#F8FAFC' },
};

export default function IncidentReportPage() {
  const { user } = useAuth();
  const [incidents, setIncidents]   = useState([]);
  const [zones, setZones]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showForm, setShowForm]     = useState(false);
  const [filter, setFilter]         = useState('OPEN');
  const [photo, setPhoto]           = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    zone_id:       '',
    incident_type: 'spill',
    severity:      'medium',
    title:         '',
    description:   '',
  });

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    const [{ data: inc }, { data: z }] = await Promise.all([
      supabase.from('grocery_incidents')
        .select('*, grocery_zones(zone_name, zone_icon, zone_color)')
        .eq(filter !== 'ALL' ? 'status' : 'id', filter !== 'ALL' ? filter : supabase.from('grocery_incidents'))
        .order('created_at', { ascending: false }),
      supabase.from('grocery_zones').select('id, zone_name, zone_icon').eq('active', true).order('zone_name'),
    ]);
    setIncidents(inc || []);
    setZones(z || []);
    setLoading(false);
  };

  const handlePhotoCapture = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhoto(file);
    const reader = new FileReader();
    reader.onload = ev => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description) return;
    setSubmitting(true);

    try {
      let photoUrl = null;
      if (photo) {
        const ext = photo.name.split('.').pop();
        const path = `grocery-incidents/${Date.now()}.${ext}`;
        const { error: upErr } = await supabase.storage.from('proof-photos').upload(path, photo, { contentType: photo.type });
        if (!upErr) {
          const { data } = supabase.storage.from('proof-photos').getPublicUrl(path);
          photoUrl = data?.publicUrl;
        }
      }

      const { data: store } = await supabase.from('grocery_stores').select('id').eq('active', true).limit(1).single();

      await supabase.from('grocery_incidents').insert({
        store_id:      store?.id,
        zone_id:       form.zone_id || null,
        reported_by:   user?.auth_user_id || user?.id,
        incident_type: form.incident_type,
        severity:      form.severity,
        title:         form.title,
        description:   form.description,
        photo_url:     photoUrl,
        status:        'OPEN',
      });

      setForm({ zone_id: '', incident_type: 'spill', severity: 'medium', title: '', description: '' });
      setPhoto(null);
      setPhotoPreview(null);
      setShowForm(false);
      fetchData();
    } catch (err) {
      console.error('Incident report failed:', err);
      alert('Failed to submit incident report.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Incident Reports</h1>
          <p className="page-subtitle">Log and track all safety and compliance incidents</p>
        </div>
        <button className="btn btn-danger" onClick={() => setShowForm(true)}>
          <AlertTriangle size={16} />
          Report Incident
        </button>
      </div>

      {/* Report Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}>
          <div className="modal-content">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <AlertTriangle size={22} color="#EF4444" />
                <span style={{ fontSize: 18, fontWeight: 700, color: '#134E4A' }}>Report Incident</span>
              </div>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Type selector */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 6 }}>Incident Type</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                  {INCIDENT_TYPES.map(t => (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, incident_type: t.value }))}
                      style={{
                        padding: '8px 6px', border: `2px solid ${form.incident_type === t.value ? '#0F766E' : '#E2E8F0'}`,
                        borderRadius: 10, background: form.incident_type === t.value ? '#CCFBF1' : '#fff',
                        cursor: 'pointer', textAlign: 'center', fontSize: 11,
                        color: form.incident_type === t.value ? '#0F766E' : '#64748B', fontWeight: 600,
                      }}
                    >
                      <div style={{ fontSize: 18, marginBottom: 2 }}>{t.icon}</div>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Severity */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 6 }}>Severity</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {Object.entries(SEVERITY_CONFIG).map(([val, cfg]) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, severity: val }))}
                      style={{
                        flex: 1, padding: '8px', border: `2px solid ${form.severity === val ? cfg.color : '#E2E8F0'}`,
                        borderRadius: 8, background: form.severity === val ? cfg.bg : '#fff',
                        cursor: 'pointer', fontSize: 12, fontWeight: 600, color: form.severity === val ? cfg.color : '#64748B',
                      }}
                    >
                      {cfg.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Zone */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 6 }}>Zone (optional)</label>
                <select className="input" value={form.zone_id} onChange={e => setForm(f => ({ ...f, zone_id: e.target.value }))}>
                  <option value="">Select zone...</option>
                  {zones.map(z => <option key={z.id} value={z.id}>{z.zone_icon} {z.zone_name}</option>)}
                </select>
              </div>

              {/* Title */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 6 }}>Incident Title *</label>
                <input
                  className="input"
                  type="text"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Brief description of the incident"
                  required
                />
              </div>

              {/* Description */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 6 }}>Details *</label>
                <textarea
                  className="input"
                  rows={3}
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Describe what happened, where, and any immediate actions taken..."
                  required
                />
              </div>

              {/* Photo */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 6 }}>Photo Evidence (optional)</label>
                {photoPreview ? (
                  <div style={{ position: 'relative', borderRadius: 10, overflow: 'hidden' }}>
                    <img src={photoPreview} alt="Incident" style={{ width: '100%', height: 160, objectFit: 'cover' }} />
                    <button
                      type="button"
                      onClick={() => { setPhoto(null); setPhotoPreview(null); }}
                      style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: 6, padding: 4, cursor: 'pointer', display: 'flex' }}
                    >
                      <X size={14} color="#fff" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      width: '100%', padding: '20px 16px', border: '2px dashed #CCFBF1',
                      borderRadius: 10, background: '#F0FDF9', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    }}
                  >
                    <Camera size={20} color="#0F766E" />
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#0F766E' }}>Take Photo</span>
                  </button>
                )}
                <input ref={fileInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handlePhotoCapture} />
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-danger btn-lg" style={{ flex: 1 }} disabled={submitting}>
                  {submitting ? 'Submitting...' : <><Send size={16} /> Submit Report</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="page-body">
        {/* Filter */}
        <div className="filter-bar" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {['OPEN', 'IN_PROGRESS', 'RESOLVED', 'ALL'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                padding: '7px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                border: 'none',
                background: filter === f ? '#0F766E' : '#CCFBF1',
                color: filter === f ? '#fff' : '#0F766E',
              }}
            >
              {f === 'ALL' ? 'All' : STATUS_CONFIG[f]?.label || f}
            </button>
          ))}
        </div>

        {incidents.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '48px 0', color: '#94A3B8' }}>
            <CheckCircle size={40} style={{ margin: '0 auto 12px', display: 'block', color: '#10B981' }} />
            No incidents found.
          </div>
        ) : (
          incidents.map(inc => {
            const sevCfg = SEVERITY_CONFIG[inc.severity] || SEVERITY_CONFIG.medium;
            const stsCfg = STATUS_CONFIG[inc.status] || STATUS_CONFIG.OPEN;
            const type   = INCIDENT_TYPES.find(t => t.value === inc.incident_type);
            return (
              <div key={inc.id} className="card" style={{ borderLeft: `4px solid ${sevCfg.color}` }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                  <div style={{ fontSize: 28, flexShrink: 0 }}>{type?.icon || '📋'}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: '#134E4A' }}>{inc.title}</span>
                      <span className="badge" style={{ background: sevCfg.bg, color: sevCfg.color }}>{sevCfg.label}</span>
                      <span className="badge" style={{ background: stsCfg.bg, color: stsCfg.color }}>{stsCfg.label}</span>
                    </div>
                    <div style={{ fontSize: 13, color: '#64748B', marginBottom: 8 }}>{inc.description}</div>
                    <div style={{ fontSize: 11, color: '#94A3B8' }}>
                      {inc.grocery_zones?.zone_icon} {inc.grocery_zones?.zone_name || 'No zone'} ·{' '}
                      {new Date(inc.created_at).toLocaleString()} ·{' '}
                      {type?.label || inc.incident_type}
                    </div>
                  </div>
                  {inc.photo_url && (
                    <img src={inc.photo_url} alt="Incident" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
